function toDateSafe(value) {
  if (!value) return null
  if (value.toDate) return value.toDate()
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** @returns {number|null} nombre de jours depuis dateA, ou null si date inconnue */
function daysBetween(dateA, dateB = new Date()) {
  if (!dateA) return null
  const ms = dateB.getTime() - dateA.getTime()
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)))
}

function average(values) {
  if (!Array.isArray(values) || values.length === 0) return 0
  return values.reduce((sum, v) => sum + (Number(v) || 0), 0) / values.length
}

function variance(values) {
  if (!Array.isArray(values) || values.length === 0) return 0
  const avg = average(values)
  return average(values.map((v) => {
    const delta = (Number(v) || 0) - avg
    return delta * delta
  }))
}

export function computeRiskScore(studentMetrics) {
  const {
    recentScores = [],
    lateAssignments = 0,
    lastActivityAt = null
  } = studentMetrics || {}

  let score = 0
  const reasons = []
  const inactivityDays = daysBetween(toDateSafe(lastActivityAt))

  if (inactivityDays != null && inactivityDays > 10) {
    score += 40
    reasons.push(`Inactif depuis ${inactivityDays} jours`)
  }
  if (lateAssignments >= 2) {
    score += 30
    reasons.push(`${lateAssignments} devoirs en retard`)
  }
  if (recentScores.length >= 3) {
    const latest = average(recentScores.slice(0, 3))
    const previous = average(recentScores.slice(3, 6))
    if (previous > 0 && latest <= previous - 15) {
      score += 20
      reasons.push('Baisse de score récente')
    }
    if (variance(recentScores.slice(0, 6)) > 280) {
      score += 10
      reasons.push('Résultats instables')
    }
  }

  return {
    score: Math.min(100, score),
    reasons,
    inactivityDays
  }
}

export function getRiskLabel(riskScore) {
  if (riskScore >= 70) return 'critique'
  if (riskScore >= 40) return 'a_surveiller'
  return 'stable'
}

export function buildStudentAlerts(studentMetricsList) {
  if (!Array.isArray(studentMetricsList)) return []
  return studentMetricsList
    .map((entry) => {
      const risk = computeRiskScore(entry)
      return {
        ...entry,
        riskScore: risk.score,
        riskLabel: getRiskLabel(risk.score),
        alertReasons: risk.reasons,
        inactivityDays: risk.inactivityDays
      }
    })
    .sort((a, b) => b.riskScore - a.riskScore)
}

export function buildPedagogicalRecommendations({ weakSkills = [], weakestAssignments = [] } = {}) {
  const recommendations = []

  weakSkills.slice(0, 3).forEach((skill) => {
    recommendations.push({
      type: 'skill',
      level: 'high',
      title: `Consolider ${skill.label}`,
      detail: `Taux de reussite ${skill.successRate}% (min 8 items). Prevoir un travail cible cette semaine.`
    })
  })

  weakestAssignments.slice(0, 2).forEach((assignment) => {
    recommendations.push({
      type: 'assignment',
      level: 'medium',
      assignmentId: assignment.assignmentId || null,
      exerciseId: assignment.exerciseId || null,
      exerciseThumbnail: assignment.exerciseThumbnail || null,
      assignmentTitle: assignment.title || 'Exercice',
      className: assignment.className || '',
      medianScore: Number(assignment.medianScore || 0),
      submissionRate: Number(assignment.submissionRate || 0),
      title: `Revoir le devoir ${assignment.title || 'Exercice'}`,
      detail: `Difficulte percue elevee (${Number(assignment.medianScore || 0)}% median, ${Number(assignment.submissionRate || 0)}% de rendus).`
    })
  })

  return recommendations
}
